import { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { debounce } from 'lodash';

export const useLocation = () => {
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isProcessingUrl, setIsProcessingUrl] = useState(false);
  const suggestionsRef = useRef(null);

  const getCurrentLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      setIsLoadingLocation(true);
      setLocationError('');

      const options = {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 300000
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setIsLoadingLocation(false);
          resolve({ lat: latitude, lng: longitude });
        },
        (error) => {
          console.error('Geolocation error:', error);
          setIsLoadingLocation(false);
          const errorMessage = 'Failed to get your location. Please enter your address manually.';
          setLocationError(errorMessage);
          reject(new Error(errorMessage));
        },
        options
      );
    });
  }, []);

  const reverseGeocode = useCallback(async (latlng) => {
    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latlng.lat}&lon=${latlng.lng}&zoom=18&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'Bl0omnav'
          }
        }
      );

      if (response.data && response.data.display_name) {
        return {
          success: true,
          address: response.data.display_name,
          coordinates: { lat: latlng.lat, lng: latlng.lng }
        };
      } else {
        return {
          success: false,
          error: 'Could not find address for this location'
        };
      }
    } catch (error) {
      console.error('Error fetching address:', error);
      return {
        success: false,
        error: 'Got your location but couldn\'t fetch the address. You can enter it manually.'
      };
    }
  }, []);

  const searchPlaces = useCallback(
    debounce(async (searchText, options = {}) => {
      if (!searchText || searchText.length < 3) {
        setLocationSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      try {
        const {
          countryCode = '',
          preferBusiness = false
        } = options || {};

        const normalizeResults = (arr) => {
          if (!Array.isArray(arr)) return [];
          return arr;
        };

        const searchWithQuery = async (query) => {
          const countryPart = countryCode ? `&countrycodes=${encodeURIComponent(countryCode)}` : '';
          const response = await axios.get(
            `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5${countryPart}&q=${encodeURIComponent(query)}`
          );
          return normalizeResults(response.data);
        };

        let results = [];
        if (preferBusiness) {
          // Business-first query for better "company/office/shop" matches.
          results = await searchWithQuery(`${searchText} business`);
        }
        if (!results.length) {
          results = await searchWithQuery(searchText);
        }

        setLocationSuggestions(results);
        setShowSuggestions(results.length > 0);
      } catch (error) {
        console.error('Error fetching location suggestions:', error);
        setLocationSuggestions([]);
        setShowSuggestions(false);
      }
    }, 500),
    []
  );

  const selectSuggestion = useCallback((suggestion) => {
    const location = {
      address: suggestion.display_name,
      coordinates: {
        lat: parseFloat(suggestion.lat),
        lng: parseFloat(suggestion.lon)
      }
    };

    setShowSuggestions(false);
    setLocationSuggestions([]);
    setLocationError('');

    return location;
  }, []);

  const clearLocationError = useCallback(() => {
    setLocationError('');
  }, []);

  const hideSuggestions = useCallback(() => {
    setShowSuggestions(false);
  }, []);

  const getCurrentLocationWithAddress = useCallback(async () => {
    try {
      const coordinates = await getCurrentLocation();
      const geocodeResult = await reverseGeocode(coordinates);
      
      if (geocodeResult.success) {
        return {
          success: true,
          location: geocodeResult
        };
      } else {
        return {
          success: true,
          location: {
            address: '',
            coordinates
          },
          warning: geocodeResult.error
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }, [getCurrentLocation, reverseGeocode]);

  // Extract coordinates from expanded Google Maps URLs
  const extractLatLng = useCallback((url) => {
    try {
      // Handle various Google Maps URL formats
      const patterns = [
        // Standard Google Maps URLs with coordinates
        /[@](-?\d+\.\d+),(-?\d+\.\d+)/,
        // URLs with !3d and !4d parameters
        /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/,
        // URLs with ll parameter
        /ll=(-?\d+\.\d+),(-?\d+\.\d+)/,
        // Query parameter format
        /q=(-?\d+\.\d+),(-?\d+\.\d+)/,
        // Another common format
        /\/maps\/@(-?\d+\.\d+),(-?\d+\.\d+)/,
        // Place ID with coordinates
        /data=.*!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/
      ];

      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) {
          const lat = parseFloat(match[1]);
          const lng = parseFloat(match[2]);
          if (!isNaN(lat) && !isNaN(lng)) {
            console.log(`Extracted coordinates using pattern: ${pattern}`, { lat, lng });
            return { lat, lng };
          }
        }
      }

      // If no coordinates found, try to extract place name or address
      const placeMatch = url.match(/\/place\/([^\/\?&]+)/);
      if (placeMatch) {
        const placeName = decodeURIComponent(placeMatch[1].replace(/\+/g, ' '));
        console.log('Extracted place name:', placeName);
        return { placeName };
      }

      console.log('No coordinates or place name found in URL:', url);
      return null;
    } catch (error) {
      console.error('Error extracting lat/lng from URL:', error);
      return null;
    }
  }, []);

  // Unshorten URLs using the unshorten.me API
  const unshortenUrl = useCallback(async (shortUrl) => {
    try {
      const response = await axios.get(`https://unshorten.me/json/${encodeURIComponent(shortUrl)}`);
      if (response.data && response.data.resolved_url) {
        return response.data.resolved_url;
      }
      return shortUrl; // Return original if unshortening fails
    } catch (error) {
      console.error('Error unshortening URL:', error);
      return shortUrl; // Return original if unshortening fails
    }
  }, []);

  // Check if a string is a Google Maps URL
  const isGoogleMapsUrl = useCallback((text) => {
    const googleMapsPatterns = [
      /maps\.google\./,
      /goo\.gl\/maps/,
      /maps\.app\.goo\.gl/,
      /maps\.app\.goo\.gle/,
      /g\.page/,
      /plus\.codes/
    ];
    
    return googleMapsPatterns.some(pattern => pattern.test(text));
  }, []);

  // Process Google Maps URL and extract location
  const processGoogleMapsUrl = useCallback(async (url) => {
    setIsProcessingUrl(true);
    setLocationError('');
    
    try {
      // First, try to unshorten the URL if it looks like a shortened URL
      let expandedUrl = url;
      if (url.includes('goo.gl') || url.includes('g.page') || url.includes('maps.app.goo.gl')) {
        console.log('Unshortening URL:', url);
        expandedUrl = await unshortenUrl(url);
        console.log('Expanded URL:', expandedUrl);
      }

      // Extract coordinates from the URL
      const extractedData = extractLatLng(expandedUrl);
      
      if (extractedData) {
        if (extractedData.lat && extractedData.lng) {
          // We have coordinates, now reverse geocode to get the address
          const geocodeResult = await reverseGeocode(extractedData);
          
          if (geocodeResult.success) {
            return {
              success: true,
              location: geocodeResult
            };
          } else {
            // Even if reverse geocoding fails, we still have coordinates
            return {
              success: true,
              location: {
                address: '',
                coordinates: { lat: extractedData.lat, lng: extractedData.lng }
              },
              warning: 'Could not get address for these coordinates, but location is set on map'
            };
          }
        } else if (extractedData.placeName) {
          // We have a place name, search for it
          try {
            const response = await axios.get(
              `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(extractedData.placeName)}&limit=1`
            );

            if (response.data && response.data.length > 0) {
              const result = response.data[0];
              return {
                success: true,
                location: {
                  address: result.display_name,
                  coordinates: {
                    lat: parseFloat(result.lat),
                    lng: parseFloat(result.lon)
                  }
                }
              };
            } else {
              return {
                success: false,
                error: 'Could not find location for the place name in the URL'
              };
            }
          } catch (error) {
            console.error('Error searching for place name:', error);
            return {
              success: false,
              error: 'Error searching for the location'
            };
          }
        }
      }

      return {
        success: false,
        error: 'Could not extract location from this Google Maps URL'
      };
    } catch (error) {
      console.error('Error processing Google Maps URL:', error);
      return {
        success: false,
        error: 'Error processing the Google Maps URL'
      };
    } finally {
      setIsProcessingUrl(false);
    }
  }, [extractLatLng, unshortenUrl, reverseGeocode]);

  return {
    isLoadingLocation,
    locationError,
    locationSuggestions,
    showSuggestions,
    suggestionsRef,
    isProcessingUrl,
    getCurrentLocation,
    getCurrentLocationWithAddress,
    reverseGeocode,
    searchPlaces,
    selectSuggestion,
    clearLocationError,
    hideSuggestions,
    setLocationError,
    isGoogleMapsUrl,
    processGoogleMapsUrl
  };
}; 